import { useContext, useEffect, useMemo, useState } from "react";
import { TranslationService } from "../../../services/TranslationService";
import ToastContext from "../../shared/bootstrap/Toast";
import ModalService from "../../shared/bootstrap/modal/Modal";
import {
  AGENDA_DASHBOARD_TYPE,
  DashboardEntity
} from "../../../entities/company/Dashboard/DashboardEntity";
import { AIAgentType } from "../../../entities/company/AIBilling/AIBilling";
import { parseIntOrDefault } from "../../../utils/ParseUtils";
import DashboardService from "../../../services/DashboardService";
import FloatingPanelService from "../../shared/FloatingPanel";
import { FloatingPanelFooter } from "../../shared/components/FloatingPanelFooter";
import {
  MultiselectEditor,
  TextEditorNumber
} from "../../shared/components/Editors";
import CompanyService from "../../../services/CompanyService";
import { ClientFields } from "../../../services/ClientService";
import DashboardFilterEditor from "./DashboardFilterEditor";

const DashboardEdit = ({
  reload,
  dashboard
}: {
  reload: () => void;
  dashboard: DashboardEntity;
}) => {
  useEffect(() => {
    // Normalizar GroupBy: convertir -7 (valor antiguo) a -29 (valor nuevo) para segmentos
    const normalizedDashboard = { ...dashboard };
    if (normalizedDashboard.GroupBy === -7) {
      normalizedDashboard.GroupBy = ClientFields.Segments;
    }
    setDashboardModel(normalizedDashboard);
  }, [dashboard]);

  const canShowVariable = () => {
    return (
      dashboardModel.Type === 0 ||
      dashboardModel.Type === 1 ||
      dashboardModel.Type === 2 ||
      dashboardModel.Type === 7 ||
      dashboardModel.Type === 8
    );
  };

  const canShowExtraData = () => {
    return (
      dashboardModel.Type === 0 ||
      dashboardModel.Type === 1 ||
      dashboardModel.Type === 2 ||
      dashboardModel.Type === 7
    );
  };

  const canShowGroupBy = () => {
    return (
      dashboardModel.Type === 0 ||
      dashboardModel.Type === 1 ||
      dashboardModel.Type === 2 ||
      dashboardModel.Type === 7 ||
      dashboardModel.Type === 11
    );
  };

  const canShowLastDays = () => {
    return (
      dashboardModel.Type === 10 ||
      dashboardModel.Type === 11 ||
      dashboardModel.Type === 12
    );
  };

  const canShowFilter = () =>
    [0, 1, 2, 3, 7, 8, 9, 10, 11, 12, 13].includes(dashboardModel.Type);

  const canShowSegments = () => {
    // Compatibilidad: -7 es el valor antiguo para segmentos, -29 es el nuevo
    return (
      dashboardModel.GroupBy === ClientFields.Segments ||
      dashboardModel.GroupBy === -7
    );
  };

  const { showToast } = useContext(ToastContext);
  const { translate } = TranslationService;
  const [submiting, setSubmiting] = useState("");
  const [dashboardModel, setDashboardModel] = useState({ ...dashboard });
  const [filterIncomplete, setFilterIncomplete] = useState(false);

  const grouping = DashboardService.getGrouping(dashboardModel.Type === 11);
  const variables = DashboardService.getVariables(dashboardModel.Type === 8);
  const panelTypes = DashboardService.getPanelTypes();
  // Agenda is offered only when its agent is enabled; an existing Agenda panel keeps its option.
  const canSelectAgenda =
    CompanyService.canShowAgentAI(AIAgentType.Agenda) ||
    dashboard.Type === AGENDA_DASHBOARD_TYPE;
  const segments = CompanyService.getSegments()
    .map((x) => ({ value: x.Id, label: x.Value }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const selectedSegments = useMemo(() => {
    if (!dashboardModel.Data) {
      return [];
    }
    const dataValues = dashboardModel.Data.split(",")
      .map((x) => x.trim())
      .filter((x) => x);
    const result = dataValues
      .map((x) => {
        // Comparar como strings para evitar problemas de tipo
        const found = segments.find((y) => String(y.value) === String(x));
        if (!found) {
        }
        return found;
      })
      .filter((x): x is { value: string; label: string } => x !== undefined);
    return result;
  }, [dashboardModel.Data, segments]);

  const dashboardTypeChanged = (type: number) => {
    dashboardModel.Type = type;
    if (canShowExtraData()) {
      if (dashboardModel.GroupBy === 0) {
        dashboardModel.GroupBy = grouping[0].Value;
      }
    } else {
      //dashboardModel.Title = "";
      dashboardModel.MaxItems = 0;
      //dashboardModel.Variable = 0;
      dashboardModel.GroupBy = 0;
    }
    if (canShowVariable()) {
      const variableOptions = DashboardService.getVariables(
        dashboardModel.Type === 8
      );
      if (!variableOptions.find((x) => x.Value === dashboardModel.Variable)) {
        dashboardModel.Variable = variableOptions[0].Value;
      }
    } else {
      dashboardModel.Variable = 0;
    }
    if (!canShowLastDays()) {
      dashboardModel.DashboardPersonDays = null;
    }
    if (type === AGENDA_DASHBOARD_TYPE) {
      // Agenda only uses the title: drop any leftover filter or data.
      dashboardModel.Filter = null;
      dashboardModel.Data = null;
    }

    setDashboardModel({ ...dashboardModel });
  };

  const groupByChanged = (groupBy: number) => {
    // Convertir -7 (valor antiguo) a -29 (valor nuevo) para segmentos
    const normalizedGroupBy = groupBy === -7 ? ClientFields.Segments : groupBy;
    setDashboardModel((prev) => {
      // Solo actualizar si el valor realmente cambió
      if (prev.GroupBy === normalizedGroupBy) {
        return prev;
      }
      return { ...prev, GroupBy: normalizedGroupBy };
    });
  };

  const segmentChanged = (values: string[] | undefined) => {
    const val = values ? values.join(",") : null;
    setDashboardModel((prev) => {
      const newModel = { ...prev, Data: val };
      return newModel;
    });
  };

  const onSubmit = async () => {
    if (submiting !== "") {
      return;
    }
    if (canShowFilter() && filterIncomplete) {
      showToast(translate.MissingRequiredFields, undefined, "danger");
      return;
    }
    setSubmiting("set");

    const result = await DashboardService.set(dashboardModel);
    if (result instanceof Error) {
      showToast(translate.ErrorProcessingRequest, undefined, "danger");
      setSubmiting("");
      return;
    }
    setSubmiting("");
    reload();
    FloatingPanelService.hidePanel();
    showToast(translate.DashboardSaved, undefined, "success");
  };

  const onDelete = () => {
    if (submiting !== "") {
      return;
    }
    const deleteDashboard = async () => {
      setSubmiting("del");
      const result = await DashboardService.delete(
        dashboardModel.DashboardID!,
        dashboardModel.DashboardPanelID
      );
      if (result instanceof Error) {
        showToast(translate.ErrorProcessingRequest, undefined, "danger");
        setSubmiting("");
        return;
      }
      setSubmiting("");
      reload();
      FloatingPanelService.hidePanel();
      showToast(translate.DashboardDeleted, undefined, "success");
    };
    ModalService.showDefaultModal({
      acceptButtonLabel: translate.Delete,
      message: translate.AreYouSureDeleteDashboard,
      onAcceptClick: deleteDashboard
    });
  };

  return (
    <>
      <div className="floatingBody p-4">
        <div className="card-body">
          <div className="form-group mb-3">
            <label>{translate.Type}:</label>
            <select
              className="form-control"
              onChange={(e) =>
                dashboardTypeChanged(parseIntOrDefault(e.target.value, 0))
              }
              value={dashboardModel.Type}
            >
              {Object.keys(panelTypes)
                .filter(
                  (key) =>
                    Number(key) !== AGENDA_DASHBOARD_TYPE || canSelectAgenda
                )
                .sort((a, b) => Number(a) - Number(b))
                .map((key) => (
                  <option key={key} value={key}>
                    {panelTypes[Number(key)]}
                  </option>
                ))}
            </select>
          </div>

          <div className="form-group mb-3">
            <label>{translate.Title}</label>
            <input
              type="text"
              className="form-control"
              value={dashboardModel.Title}
              onChange={(e) =>
                setDashboardModel({ ...dashboardModel, Title: e.target.value })
              }
            ></input>
          </div>

          {canShowVariable() && (
            <div className="form-group mb-3">
              <label>{translate.Variable}</label>
              <select
                className="form-control"
                onChange={(e) =>
                  setDashboardModel({
                    ...dashboardModel,
                    Variable: parseIntOrDefault(e.target.value, 0)
                  })
                }
                value={dashboardModel.Variable}
              >
                {variables.map((x) => (
                  <option key={x.Value} value={x.Value}>
                    {x.Text}
                  </option>
                ))}
              </select>
            </div>
          )}
          {canShowGroupBy() && (
            <>
              <div className="form-group mb-3">
                <label>{translate.GroupedBy}</label>
                <select
                  className="form-control"
                  value={dashboardModel.GroupBy}
                  onChange={(e) =>
                    groupByChanged(parseIntOrDefault(e.target.value, 0))
                  }
                >
                  {grouping.map((x) => (
                    <option key={x.Value} value={x.Value}>
                      {x.Text}
                    </option>
                  ))}
                </select>
              </div>
              {canShowSegments() &&
                (() => {
                  return (
                    <div className="form-group mb-3">
                      <label>{translate.Segments}</label>
                      <MultiselectEditor
                        placeholder={translate.Select}
                        callback={segmentChanged}
                        value={selectedSegments}
                        items={segments}
                      />
                    </div>
                  );
                })()}
            </>
          )}
          {canShowExtraData() && (
            <div className="form-group mb-3">
              <label>{translate.ItemsLimit}</label>
              <div className="d-flex">
                <input
                  type="text"
                  style={{ width: "75px" }}
                  className="form-control"
                  value={dashboardModel.MaxItems ?? 0}
                  onChange={(e) =>
                    setDashboardModel({
                      ...dashboardModel,
                      MaxItems: parseIntOrDefault(e.target.value, 0)
                    })
                  }
                ></input>
                <label className="font-weight-normal col-form-label ps-2">
                  0: {translate.NoLimit}
                </label>
              </div>
            </div>
          )}

          {canShowLastDays() && (
            <div className="form-group mb-3">
              <label>{translate.LastNDays}</label>
              <div className="d-flex">
                <TextEditorNumber
                  value={dashboardModel.Data ?? ""}
                  onChange={(value) =>
                    setDashboardModel({
                      ...dashboardModel,
                      Data: parseIntOrDefault(value, 0).toString()
                    })
                  }
                  style={{ width: "75px" }}
                />
              </div>
            </div>
          )}

          {canShowFilter() && (
            <div className="form-group mb-3">
              <label>{translate.Filters}</label>
              <DashboardFilterEditor
                value={dashboardModel.Filter ?? ""}
                onChange={(filter) =>
                  setDashboardModel({
                    ...dashboardModel,
                    Filter: filter || null
                  })
                }
                onHasIncomplete={setFilterIncomplete}
              />
            </div>
          )}
        </div>
      </div>

      <FloatingPanelFooter>
        {dashboard.DashboardID > 0 && (
          <button
            type="button"
            className="btn btn-danger btn-sm"
            onClick={onDelete}
          >
            {translate.Delete}
            {submiting === "del" && (
              <i className="fas fa-spinner-third fa-spin third ms-2"></i>
            )}
          </button>
        )}

        {CompanyService.canDo("editdashboard") && (
          <button className="btn btn-primary btn-sm" onClick={onSubmit}>
            {translate.Save}
            {submiting === "set" && (
              <i className="fas fa-spinner-third fa-spin third ms-2"></i>
            )}
          </button>
        )}
      </FloatingPanelFooter>
    </>
  );
};

export default DashboardEdit;
